import ArrowDropDownRounded from '@mui/icons-material/ArrowDropDownRounded';
import ArrowDropUpRounded from '@mui/icons-material/ArrowDropUpRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import {
    Box,
    CircularProgress,
    IconButton,
    Skeleton,
    Stack,
    Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import React from 'react';
import { Sparkline } from '../../../components/Sparkline.js';
import { polygonApi } from '../../../services/polygon.js';

type WatchEntry = {
  symbol: string;
  name?: string;
  price: number;
  change: number;
  changePercent: number;
  sparkline: number[];
};

const FALLBACK_WATCHLIST: WatchEntry[] = [
  { symbol: 'AAPL', name: 'Apple', price: 242.18, change: 1.26, changePercent: 0.52, sparkline: [0, 0.12, 0.2, 0.25, 0.3, 0.28, 0.33] },
  { symbol: 'MSFT', name: 'Microsoft', price: 140.11, change: -2.39, changePercent: -1.67, sparkline: [0, 0.05, 0.08, 0.02, -0.05, -0.1, -0.16] },
  { symbol: 'AMZN', name: 'Amazon', price: 187.51, change: 3.21, changePercent: 1.74, sparkline: [0, 0.1, 0.21, 0.18, 0.25, 0.35, 0.4] },
  { symbol: 'GOOGL', name: 'Alphabet', price: 147.31, change: 0.94, changePercent: 0.64, sparkline: [0, 0.06, 0.14, 0.1, 0.16, 0.18, 0.22] },
  { symbol: 'NVDA', name: 'NVIDIA', price: 1421.1, change: 8.14, changePercent: 0.58, sparkline: [0, 0.08, 0.18, 0.21, 0.29, 0.26, 0.35] },
  { symbol: 'META', name: 'Meta', price: 562.11, change: -1.93, changePercent: -1.37, sparkline: [0, 0.05, -0.12, -0.05, -0.14, -0.12, -0.18] },
  { symbol: 'COST', name: 'Costco', price: 1080.1, change: 2.06, changePercent: 0.27, sparkline: [0, 0.07, 0.12, 0.09, 0.11, 0.15, 0.2] },
  { symbol: 'NFLX', name: 'Netflix', price: 745.51, change: 3.3, changePercent: 0.16, sparkline: [0, 0.1, 0.18, 0.22, 0.2, 0.24, 0.28] },
];

interface MarketPulseStripProps {
  symbols: string[];
  selectedSymbol?: string | null;
  onSelectSymbol?: (symbol: string) => void;
  onRemoveSymbol?: (symbol: string) => void;
  removingSymbols?: Set<string>;
  removeEnabled?: boolean;
}

const WatchTile: React.FC<{
  entry: WatchEntry;
  selected?: boolean;
  onSelect?: (symbol: string) => void;
  onRemove?: (symbol: string) => void;
  removing?: boolean;
  removeEnabled?: boolean;
}> = ({ entry, selected, onSelect, onRemove, removing, removeEnabled }) => {
  const [showPercent, setShowPercent] = React.useState(false);

  React.useEffect(() => {
    const timer = setInterval(() => {
      setShowPercent((prev) => !prev);
    }, 2000);
    return () => clearInterval(timer);
  }, []);

  const positive = entry.change >= 0;
  const accent = positive ? '#46e4b1' : '#da5947';
  const background = '#20202e';
  const chartGradient = `linear-gradient(90deg, rgba(32,32,46,0) 0%, rgba(32,32,46,0.4) 40%, rgba(32,32,46,0.9) 100%)`;

  const canRemove = removeEnabled && typeof onRemove === 'function';

  const displayValue = showPercent
    ? `${entry.changePercent >= 0 ? '+' : ''}${entry.changePercent.toFixed(2)}%`
    : `${entry.change >= 0 ? '+' : '-'}$${Math.abs(entry.change).toFixed(2)}`;

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={() => onSelect?.(entry.symbol)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect?.(entry.symbol);
        }
      }}
      sx={{
        position: 'relative',
        minWidth: 170,
        maxWidth: 186,
        height: 70,
        px: 1.25,
        py: 1,
        borderRadius: 1.25,
        border: `1px solid ${alpha('#ffffff', selected ? 0.14 : 0.08)}`,
        background,
        boxShadow: '0 14px 30px rgba(0,0,0,0.4)',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 1,
        cursor: 'pointer',
        transition: 'transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease',
        outline: 'none',
        '&:hover .mp-remove-btn': {
          opacity: canRemove && !removing ? 1 : 0.4,
          visibility: canRemove ? 'visible' : 'hidden',
        },
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 18px 34px rgba(0,0,0,0.5)',
          borderColor: alpha(accent, 0.35),
        },
        '&:focus-visible': {
          borderColor: alpha(accent, 0.8),
          boxShadow: '0 0 0 2px rgba(218, 89, 71, 0.35)',
        },
      }}
    >
      {canRemove && (
        <IconButton
          size="small"
          aria-label={`Remove ${entry.symbol} from portfolio`}
          className="mp-remove-btn"
          onClick={(event) => {
            event.stopPropagation();
            onRemove?.(entry.symbol);
          }}
          sx={{
            position: 'absolute',
            top: 6,
            right: 6,
            opacity: 0,
            visibility: 'hidden',
            transition: 'opacity 0.15s ease',
            color: '#d2d5db',
            backgroundColor: alpha('#000', 0.4),
            zIndex: 2, // keep above sparkline/gradients so it stays clickable
            pointerEvents: 'auto',
            '&:hover': { backgroundColor: alpha('#000', 0.6) },
          }}
        >
          {removing ? <CircularProgress size={14} color="inherit" /> : <CloseRounded fontSize="small" />}
        </IconButton>
      )}

      <Stack spacing={0.4} sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="subtitle1"
          sx={{
            color: alpha('#d2d5db', 0.85),
            letterSpacing: 0.2,
            fontWeight: 600,
            lineHeight: 1.2,
            fontSize: 15,
            textTransform: 'uppercase',
          }}
          noWrap
        >
          {entry.symbol}
        </Typography>
        <Stack direction="row" spacing={0.5} alignItems="center">
          {positive ? (
            <ArrowDropUpRounded
              sx={{ fontSize: 18, color: accent, opacity: 0.9, transform: 'translateY(-1px)' }}
            />
          ) : (
            <ArrowDropDownRounded
              sx={{ fontSize: 18, color: accent, opacity: 0.9, transform: 'translateY(-1px)' }}
            />
          )}
          <Typography
            variant="subtitle2"
            sx={{ color: accent, fontSize: 12, letterSpacing: 0.1, minWidth: 60 }}
          >
            {displayValue}
          </Typography>
        </Stack>
      </Stack>

      <Box
        sx={{
          width: 90,
          height: 48,
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: chartGradient,
            borderRadius: 2,
            pointerEvents: 'none',
          }}
        />
        <Sparkline data={entry.sparkline} color={accent} width="100%" height="100%" />
      </Box>
    </Box>
  );
};

export const MarketPulseStrip: React.FC<MarketPulseStripProps> = ({
  symbols,
  selectedSymbol,
  onSelectSymbol,
  onRemoveSymbol,
  removingSymbols,
  removeEnabled = false,
}) => {
  const theme = useTheme();
  const [entries, setEntries] = React.useState<WatchEntry[]>(FALLBACK_WATCHLIST);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const normalizedSymbols = React.useMemo(
    () =>
      Array.from(
        new Set(
          symbols
            .map((s) => s?.trim().toUpperCase())
            .filter((s): s is string => Boolean(s))
        )
      ).slice(0, 10),
    [symbols]
  );

  const loadEntries = React.useCallback(async () => {
    if (normalizedSymbols.length === 0) {
      setEntries(FALLBACK_WATCHLIST);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const combined = await polygonApi.getBatchCombinedData(normalizedSymbols, 7);
      const mapped = combined
        .map<WatchEntry>((item) => {
          const closes = (item.historicalData || []).map((d) => d.close).filter((n) => Number.isFinite(n));
          const lastClose = closes[closes.length - 1] ?? item.quote?.lastPrice ?? 0;
          const prevClose = closes.length > 1 ? closes[closes.length - 2] : item.quote?.lastPrice ?? lastClose;
          const change = lastClose - prevClose;
          const changePercent = prevClose ? (change / prevClose) * 100 : 0;
          return {
            symbol: item.symbol?.toUpperCase?.() || item.quote?.symbol?.toUpperCase?.() || '',
            name: item.companyInfo?.name || item.symbol,
            price: lastClose,
            change,
            changePercent,
            sparkline: closes.length ? closes.slice(-14) : [lastClose, lastClose * 1.005, lastClose * 0.998],
          };
        })
        .filter((entry) => Boolean(entry.symbol));

      if (mapped.length > 0) {
        setEntries(mapped);
        return;
      }
      setEntries(FALLBACK_WATCHLIST);
    } catch (err) {
      console.warn('[MarketPulseStrip] Failed to load watchlist data', err);
      setError(err instanceof Error ? err.message : 'Unable to load market data');
      setEntries(FALLBACK_WATCHLIST);
    } finally {
      setLoading(false);
    }
  }, [normalizedSymbols]);

  React.useEffect(() => {
    void loadEntries();
  }, [loadEntries]);

  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: '10px',
        overflow: 'hidden',
        backgroundColor: '#14121a',
        border: '1px solid rgba(255,255,255,0.04)',
        boxShadow: '0 25px 60px rgba(2,4,15,0.55)',
        p: { xs: 2, md: 2.5 },
        mb: 3,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: `
            radial-gradient(60% 120% at 8% 0%, rgba(255,255,255,0.06), transparent 35%),
            radial-gradient(40% 120% at 60% -10%, rgba(255,255,255,0.05), transparent 30%),
            linear-gradient(180deg, rgba(255,255,255,0.03) 0%, transparent 60%)
          `,
          pointerEvents: 'none',
          opacity: 0.9,
        }}
      />

      <Stack spacing={2} sx={{ position: 'relative' }}>
        <Box
          sx={{
            display: 'flex',
            gap: 1.25,
            overflowX: 'auto',
            pb: 0.5,
            pt: 0.5,
            pr: 0.5,
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar': { height: 6 },
            '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(255,255,255,0.25)', borderRadius: 999 },
          }}
        >
          {loading && (
            <>
              {[...Array(6)].map((_, idx) => (
                <Skeleton key={idx} variant="rounded" width={180} height={96} sx={{ flexShrink: 0 }} />
              ))}
            </>
          )}
          {!loading &&
            entries.map((entry) => (
              <WatchTile
                key={entry.symbol}
                entry={entry}
                selected={selectedSymbol?.toUpperCase?.() === entry.symbol}
                onSelect={onSelectSymbol}
                onRemove={removeEnabled ? onRemoveSymbol : undefined}
                removeEnabled={removeEnabled}
                removing={Boolean(removingSymbols?.has(entry.symbol))}
              />
            ))}
        </Box>
        {error && (
          <Typography variant="caption" color="error" sx={{ opacity: 0.8 }}>
            {error}
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

export default MarketPulseStrip;
